import { brokenClock } from "#webapp/src/assets/img/illustrations";
import { logoClockWhite } from "#webapp/src/assets/img/logos";
import { AuthPermissionInfoCard } from "#webapp/src/components/auth-permission-info-card";
import { LoginBtn } from "#webapp/src/components/login-btn";
import { paths, supportEmail } from "#webapp/src/constants/site.constant";
import { extensionPoppedOpenTimestamp, lastSignInDate } from "#webapp/src/state/local-storage";
import { PageEvents, TrackingEvents, page, track } from "#webapp/src/util/analytics.util";
import { isPersonalDomain } from "#webapp/src/util/email.util";
import { setLoginTrackingEvents } from "#webapp/src/util/loginTrackingEvents.util";
import { PostMessageManager } from "#webapp/src/util/post-message.util";
import { Link } from "@clockwise/design-system";
import { SyncProblem } from "@clockwise/design-system/icons";
import { CookieDialog } from "@clockwise/web-commons/src/components/cookie-dialog";
import { SidebarDialog } from "@clockwise/web-commons/src/components/sidebar-dialog";
import { CalloutCard } from "@clockwise/web-commons/src/ui/callout-card";
import { showUberCantConnect } from "@clockwise/web-commons/src/util/browser.util";
import { Button, DialogActions, DialogContent, LinearProgress } from "@material-ui/core";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import { IReduxState } from "../../state/reducers/root.reducer";
import { chromeLoginFragments } from "./ChromeLogin.gql";
import * as s from "./ChromeLogin.styles";
import { IContainer, IProps, IState } from "./ChromeLoginTypes";

//////////////////
// COMPONENT
//////////////////
export class ChromeLoginComponent extends React.Component<IProps, IState> {
  private permissionInfoCardTimer: ReturnType<typeof setTimeout>;
  constructor(props: IProps) {
    super(props);
    this.state = {
      loggingIn: false,
      hidePermissionInfoCard: true,
      hideCookieErrorDialog: true,
      hideGenericErrorDialog: true,
    };
  }

  private openExtension = () => {
    PostMessageManager.toggleSidebar(true, true);
    extensionPoppedOpenTimestamp.set(DateTime.now().toISO());
    track(TrackingEvents.AUTH.POPPED_OPEN_CANT_CONNECT, {
      unauthedCalendarId: this.props.windowUserEmail,
    });
  };

  public componentDidMount() {
    page(PageEvents.CHROME_LOGIN);
    if (showUberCantConnect(this.props.windowUserEmail)) {
      PostMessageManager.setSignInNeeded(true);
      try {
        // Wrapping in try/catch to catch errors in doing fromISO from localStorage value.
        const lastPoppedOpen = DateTime.fromISO(extensionPoppedOpenTimestamp.get() || "");
        if (
          !extensionPoppedOpenTimestamp.get() ||
          (lastPoppedOpen.isValid && // If we haven't yet opened the extension this week, open it.
            lastPoppedOpen.startOf("week") < DateTime.now().startOf("week"))
        ) {
          this.openExtension();
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  private onStartLogin = () => {
    clearTimeout(this.permissionInfoCardTimer);
    track(TrackingEvents.AUTH.BEGIN_LOGIN, {
      isUberUser: showUberCantConnect(this.props.windowUserEmail),
      unauthedCalendarId: this.props.windowUserEmail,
    });
    this.setState({ loggingIn: true });
    if (this.props.onStart) {
      this.props.onStart();
    }
  };

  private onFail = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
    this.setState({ loggingIn: false });
    if (this.props.onFailure) {
      this.props.onFailure();
    }
  };

  private onGoogleFailure = (e: any) => {
    if (e.error === "popup_closed_by_user") {
      this.setState({ hidePermissionInfoCard: false });
    } else if (e.error === "idpiframe_initialization_failed") {
      this.setState({ hideCookieErrorDialog: false });
    } else {
      this.setState({ hideGenericErrorDialog: false });
    }
  };

  private onClickClose = () => {
    PostMessageManager.closeSidebar(true);
  };

  private onClickLogin = () => {
    this.permissionInfoCardTimer = setTimeout(() => {
      this.setState({ hidePermissionInfoCard: false });
    }, 10000);
  };

  private onCloseErrorDialog = () => {
    this.setState({
      hideGenericErrorDialog: true,
    });
  };

  private renderPersonalEmail = () => {
    return (
      <div className="cw-flex cw-flex-col cw-items-center cw-p-2 cw-pt-4">
        <div className="cw-heading-base cw-mb-2 cw-text-center">
          Please sign in to Clockwise from your work calendar.
        </div>
        <Button style={{ marginTop: 20 }} onClick={this.onClickClose} variant="text" fullWidth>
          Hide extension
        </Button>
      </div>
    );
  };

  private handleLoginSuccess = () => {
    lastSignInDate.set(DateTime.now().toISO());
    setLoginTrackingEvents();
    this.props?.onSuccess?.();
  };

  private renderLogin = () => {
    const { windowUserEmail } = this.props;

    let redirect;
    if (this.props.redirect) {
      redirect = this.props.redirect;
      if (this.props.location && this.props.location.search) {
        redirect += this.props.location.search;
      }
    }

    if (windowUserEmail && isPersonalDomain(windowUserEmail)) {
      return this.renderPersonalEmail();
    }

    return (
      <div className="cw-flex cw-flex-col cw-items-center cw-p-2 cw-pt-4">
        <div className="cw-heading-base cw-mb-2 cw-text-center">
          {showUberCantConnect(windowUserEmail) ? (
            <div className="cw-leading-5 cw-mt-4 cw-mb-5">
              <CalloutCard
                sentiment="warning"
                content={{
                  header: "Clockwise can't connect to your calendar",
                }}
                icon={
                  <SyncProblem className="cw-fill-warning cw-text-22 -cw-mt-[3px] -cw-mr-[9px] cw-ml-0.5" />
                }
              />
            </div>
          ) : (
            "Sign in to Clockwise with your work calendar"
          )}
        </div>
        <div className="cw-pt-3">
          <LoginBtn
            label="Sign in with Google"
            style={s.signupBtn.base}
            labelStyle={s.signupBtn.label}
            redirect={redirect}
            onStart={() => this.onStartLogin()}
            onSuccess={this.handleLoginSuccess}
            onFailure={() => this.onFail()}
            onGoogleFailure={this.onGoogleFailure}
            onClick={() => this.onClickLogin()}
          />
        </div>
        {this.state.loggingIn && (
          <LinearProgress className="cw-w-full cw-mt-3" variant="indeterminate" />
        )}
        <div className="cw-caption cw-text-muted cw-w-[250px] cw-mt-3 cw-text-center">
          By signing in, you agree to Clockwise's{" "}
          <Link target="_blank" href={paths.terms}>
            {" "}
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link target="_blank" href={paths.privacy}>
            Privacy Policy
          </Link>
          .
        </div>
      </div>
    );
  };

  private renderCookieDialog() {
    return (
      <CookieDialog
        isChromeExtension={this.props.isChromeExtension}
        open={!this.state.hideCookieErrorDialog}
      />
    );
  }

  private renderErrorDialog() {
    return (
      <SidebarDialog
        open={!this.state.hideGenericErrorDialog}
        onClose={this.onCloseErrorDialog}
        isChromeExtension={this.props.isChromeExtension}
      >
        <DialogContent>
          <div className="cw-heading-sm cw-mt-5 cw-mb-4">
            Clockwise was unable to connect your Google account
          </div>
          <div className="cw-body-base cw-text-muted cw-mb-4">
            There could be restrictions on your GSuite organization settings that would prevent
            Clockwise from accessing needed permissions.
          </div>
          <div className="cw-body-base cw-text-muted cw-mt-5">
            Please{" "}
            <Link href={`mailto:${supportEmail}`} target="_blank">
              reach out to our support team
            </Link>{" "}
            and we will do our best to help get you connected.
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={this.onCloseErrorDialog}>
            Got it
          </Button>
        </DialogActions>
      </SidebarDialog>
    );
  }

  private renderAuthErrorDialog() {
    if (!this.state.hideCookieErrorDialog) {
      return this.renderCookieDialog();
    } else if (!this.state.hideGenericErrorDialog) {
      return this.renderErrorDialog();
    }

    return null;
  }

  public render() {
    return (
      <div style={s.container}>
        <div style={s.content.container}>
          {showUberCantConnect(this.props.windowUserEmail) ? (
            <img className="cw-w-[160px]" src={brokenClock} />
          ) : (
            <div className="cw-flex cw-items-center cw-justify-center cw-w-[110px] cw-h-[110px] cw-rounded-full cw-bg-brand-emphasis">
              <img src={logoClockWhite} className="cw-w-[70px]" />
            </div>
          )}
          {this.renderLogin()}
        </div>
        {!this.state.hidePermissionInfoCard && (
          <AuthPermissionInfoCard onClose={() => this.setState({ hidePermissionInfoCard: true })} />
        )}
        {this.renderAuthErrorDialog()}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    windowUserEmail: state.webExtension.windowUserEmail,
    isChromeExtension: state.webExtension.isWebExtension,
  };
}

const reduxContainer = connect(mapStateToProps, {})(ChromeLoginComponent);

//////////////////
// RELAY
//////////////////
export const ChromeLogin = createFragmentContainer<IContainer>(
  reduxContainer,
  chromeLoginFragments,
);
